import { useFieldArray } from "react-hook-form";
import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { CustomTextField, InputPropsStyleForInvoice } from "../../helper";
import addTicketStyles from "./AddTickets.module.css";
import { Close, CheckCircle } from "@mui/icons-material";
import { Button, CircularProgress, Alert } from "@mui/material";
import { VERIFY_TICKET_IN_LOGITIX } from "./api/addTicketsQuery";
import ToasterWithMessage from "../../components/shared-components/toaster";

const InputField = CustomTextField();

const TicketsList: React.FC<{ control: any; eventIndex: number, errors: any, watch: any, setValue: any, trigger: any }> = ({ control, eventIndex, errors, watch, setValue, trigger }) => {
    const { fields } = useFieldArray({
        control,
        name: `ticketDetails.${eventIndex}.tickets`
    });
    const [verifying, setVerifying] = useState<{ [key: number]: boolean }>({});
    const [validationResults, setValidationResults] = useState<{ [key: number]: any }>({});
    const { success, error } = ToasterWithMessage();

    const [verifyTicketInLogitix] = useMutation(VERIFY_TICKET_IN_LOGITIX, {
        fetchPolicy: "no-cache",
    });

    const watchedTickets = watch(`ticketDetails.${eventIndex}`);
    const eventId = watchedTickets?.eventId;

    const handleChangeTicketDetails = (e: any, key: string) => {
        setValue(key, e.target.value);
        trigger();
        const ticketIndexMatch = key.match(/tickets\.(\d+)\./);
        if (ticketIndexMatch) {
            const idx = parseInt(ticketIndexMatch[1]);
            setValidationResults(prev => ({ ...prev, [idx]: null }));
        }
    }

    const handleVerifyTicket = async (ticketIndex: number) => {
        setVerifying(prev => ({ ...prev, [ticketIndex]: true }));
        setValidationResults(prev => ({ ...prev, [ticketIndex]: null }));

        try {
            const tickets = watch(`ticketDetails.${eventIndex}.tickets`);
            const ticket = tickets[ticketIndex];

            if (!ticket.section || !ticket.row || !ticket.seatNo || ticket.seatNo.length === 0) {
                error('Please fill in all ticket details before verifying');
                setVerifying(prev => ({ ...prev, [ticketIndex]: false }));
                return;
            }

            const seatNumbers = ticket.seatNo.map((seat: any) => seat.value).filter((val: string) => val);

            if (seatNumbers.length === 0) {
                error('Please enter at least one seat number');
                setVerifying(prev => ({ ...prev, [ticketIndex]: false }));
                return;
            }

            const { data } = await verifyTicketInLogitix({
                variables: {
                    verifyTicketInLogitixInput: {
                        eventId: eventId,
                        section: ticket.section,
                        row: ticket.row,
                        seatNumbers: seatNumbers
                    }
                }
            });

            const result = data?.verifyTicketInLogitix;
            setValidationResults(prev => ({ ...prev, [ticketIndex]: result || {} }));

            if (result?.valid) {
                success(result.message || 'Ticket verified successfully!');
            } else if (result?.error) {
                error(result.error);
            }
        } catch (err: any) {
            console.error('Verification error:', err);
            const errorMessage = err?.message || err?.graphQLErrors?.[0]?.message || 'Failed to verify ticket in Logitix';
            error(errorMessage);
            setValidationResults(prev => ({ 
                ...prev, 
                [ticketIndex]: { valid: false, error: errorMessage } 
            }));
        } finally {
            setVerifying(prev => ({ ...prev, [ticketIndex]: false }));
        }
    };

    return (
        <div>
            {fields.map((_ticket, ticketIndex) => (
                <div className={addTicketStyles.ticketDetail} key={`ticket_${ticketIndex}`}>
                    <div className={addTicketStyles.ticketContainer}>
                        <div className={addTicketStyles.ticketsubContainer}>
                            <div>Enter Section</div>
                            <div>
                                <InputField
                                    key={`ticketDetails.${eventIndex}.tickets.${ticketIndex}.section`}
                                    sx={{
                                        "& input": {
                                            height: "0.0375em",
                                            textTransform: "capitalize"
                                        },
                                    }}
                                    classes={{
                                        root: addTicketStyles.sectiondiv,
                                    }}
                                    {...control.register(`ticketDetails.${eventIndex}.tickets.${ticketIndex}.section`, {
                                        required: "Section is required!",
                                        pattern: {
                                            value: /^[a-zA-Z0-9\s\-'._/,&()]+$/,
                                            message: 'Enter valid section'
                                        },
                                        maxLength: {
                                            value: 32,
                                            message: "Maximum of 32 characters",
                                        },
                                    })}
                                    InputProps={{
                                        style: InputPropsStyleForInvoice,
                                    }}
                                     name={"section"}
                                    placeholder={`Enter section`}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeTicketDetails(e, `ticketDetails.${eventIndex}.tickets.${ticketIndex}.section`)}
                                    error={Boolean(errors?.ticketDetails?.[eventIndex]?.tickets?.[ticketIndex]?.section?.message)}
                                />
                                <span className={addTicketStyles.errorMsgStyle}>
                                    {errors?.ticketDetails?.[eventIndex]?.tickets?.[ticketIndex]?.section?.message}
                                </span>
                            </div>
                        </div>

                        <div className={addTicketStyles.ticketsubContainer}>
                            <div>Enter Row</div>
                            <div>
                                <InputField
                                    key={`ticketDetails.${eventIndex}.tickets.${ticketIndex}.row`}
                                    sx={{
                                        "& input": {
                                            height: "0.0375em",
                                            textTransform: "capitalize"
                                        },
                                    }}
                                    classes={{
                                        root: addTicketStyles.sectiondiv,
                                    }}
                                    {...control.register(`ticketDetails.${eventIndex}.tickets.${ticketIndex}.row`, {
                                        required: "Row is required!",
                                        pattern: {
                                            value: /^[a-zA-Z0-9\s\-'._/,&()]+$/,
                                            message: 'Enter valid row'
                                        },
                                        maxLength: {
                                            value: 32,
                                            message: "Maximum of 32 characters",
                                        },
                                    })}
                                    InputProps={{
                                        style: InputPropsStyleForInvoice,
                                    }}
                                     name="row"
                                    placeholder={`Enter row`}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeTicketDetails(e, `ticketDetails.${eventIndex}.tickets.${ticketIndex}.row`)}
                                    error={Boolean(errors?.ticketDetails?.[eventIndex]?.tickets?.[ticketIndex]?.row?.message)}
                                />
                                <span className={addTicketStyles.errorMsgStyle}>
                                    {errors?.ticketDetails?.[eventIndex]?.tickets?.[ticketIndex]?.row?.message}
                                </span>
                            </div>
                        </div>
                    </div>

                    <SeatNumbers control={control} eventIndex={eventIndex} ticketIndex={ticketIndex} errors={errors} watch={watch}
                      setValue={setValue} trigger = {trigger}/>

                    <div style={{ marginTop: "15px", marginBottom: "10px" }}>
                        <Button
                            variant="outlined"
                            onClick={() => handleVerifyTicket(ticketIndex)}
                            disabled={verifying[ticketIndex]}
                            startIcon={verifying[ticketIndex] ? <CircularProgress size={16} /> : <CheckCircle />}
                            style={{ textTransform: "none" }}
                        >
                            {verifying[ticketIndex] ? 'Verifying...' : 'Verify in Logitix'}
                        </Button>
                    </div>

                    {validationResults[ticketIndex] && (
                        <Alert 
                            severity={validationResults[ticketIndex].valid ? 'success' : 'error'}
                            style={{ marginTop: "10px", marginBottom: "10px" }}
                        >
                            {validationResults[ticketIndex].valid 
                                ? validationResults[ticketIndex].message 
                                : (
                                    <>
                                        {validationResults[ticketIndex].error}
                                        {validationResults[ticketIndex].availableSectionsHierarchy?.length > 0 ? (
                                            <div style={{ marginTop: "12px", fontFamily: "monospace", fontSize: "13px", whiteSpace: "pre-wrap" }}>
                                                <strong>Available sections with rows and seats:</strong>
                                                {validationResults[ticketIndex].availableSectionsHierarchy.map((s: { section: string; rows: { row: string; seats: string[] }[] }) => (
                                                    <div key={s.section} style={{ marginTop: "6px", marginLeft: "8px" }}>
                                                        <div>Section {s.section} - Rows: {s.rows.map((r) => r.row).join(', ')}</div>
                                                        {s.rows.map((r) => (
                                                            <div key={`${s.section}-${r.row}`} style={{ marginLeft: "16px" }}>
                                                                Row {r.row} - Seats: {r.seats.join(', ')}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <>
                                                {validationResults[ticketIndex].availableSections?.length > 0 && (
                                                    <div style={{ marginTop: "8px" }}>
                                                        <strong>Available sections:</strong> {validationResults[ticketIndex].availableSections.join(', ')}
                                                    </div>
                                                )}
                                                {validationResults[ticketIndex].availableRows?.length > 0 && (
                                                    <div style={{ marginTop: "8px" }}>
                                                        <strong>Available rows:</strong> {validationResults[ticketIndex].availableRows.join(', ')}
                                                    </div>
                                                )}
                                                {validationResults[ticketIndex].availableSeats?.length > 0 && (
                                                    <div style={{ marginTop: "8px" }}>
                                                        <strong>Available seats:</strong> {validationResults[ticketIndex].availableSeats.join(', ')}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                        </Alert>
                    )}
                </div>
            ))}
        </div>
    );
};

const SeatNumbers: React.FC<{ control: any; eventIndex: number; ticketIndex: number, errors: any, watch: any, setValue: any;trigger: any }> = ({
    control,
    eventIndex,
    ticketIndex,
    errors,
    watch,
    setValue,
    trigger
}) => {
    const [seatNoError, setSeatnoError] = useState("");
    const watchedFields = watch(['ticketDetails']);
    const seats = watch(`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo`) || [];
    useEffect(() => {
        if (errors?.ticketDetails) {
            const regex = /^[0-9]+(?: [0-9]+)*$/;

            const ticketValues = Object.values(errors.ticketDetails);
            const isErrorSeatLength = ticketValues.some((ticketvalue: any) => ticketvalue?.tickets.some((ticket: any) => ticket?.seatNo?.length === 0))
            // const isErrorSeatLength = ticketValues.some((ticket: any) => ticket?.seatNo?.length === 0);
            const isErrorSeatRegex = ticketValues.some((ticketValue: any) => ticketValue?.tickets.some((ticket: any) => ticket?.seatNo && !regex.test(ticket.seatNo)))
            // const isErrorSeatRegex = ticketValues.some((ticket: any) => ticket?.seatNo && !regex.test(ticket.seatNo));


            if (isErrorSeatLength) {
                setSeatnoError("Seat no is required!");
            } else if (isErrorSeatRegex) {
                setSeatnoError("Enter valid seat no!");
            } else {
                setSeatnoError("");
            }
        } else {
            setSeatnoError("");
        }
    }, [errors, watchedFields]);

    const handleRemoveSeat = (seatIndex: number) => {
        const updatedSeats = seats.filter((_: any, index: number) => index !== seatIndex);
        setValue(`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo`, updatedSeats);
        trigger();
    };
    return (
        <div className={addTicketStyles.seatNoContainer} key = {`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo`}>
            <div>Enter Seat No</div>
            <div>
                <div className={addTicketStyles.seatNoDiv}>

                    {seats.map((_seat: any, seatIndex: number) => (
                        <div key ={`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo.${seatIndex}.value`}
                        className={addTicketStyles.seatInlineBlock}>
                            <InputField
                                key={`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo.${seatIndex}.value`}
                                sx={{
                                    "& input": {
                                        height: "0.0375em",
                                        textTransform: "capitalize",
                                    },
                                }}
                                classes={{
                                    root: addTicketStyles.seatnodiv,
                                }}
                                {...control.register(`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo.${seatIndex}.value`, {
                                    required: "Seat no is required!",

                                    pattern: {
                                        value: /^[0-9]+(?: [0-9]+)*$/,
                                        message: 'Enter valid seat no'
                                    },
                                    maxLength: {
                                        value: 32,
                                        message: "Maximum of 32 digits are allowed",
                                    },
                                })}
                                InputProps={{
                                    style: InputPropsStyleForInvoice,
                                }}
                                value={seats[seatIndex]?.value || ""}
                                onChange={(e) => {
                                    const updatedSeats = [...seats];
                                    updatedSeats[seatIndex] = { value: e.target.value };
                                    setValue(`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo`, updatedSeats);
                                    trigger();
                                }}
    
                                error={Boolean(errors?.ticketDetails?.[eventIndex]?.tickets?.[ticketIndex]?.seatNo?.[seatIndex]?.message)}
                            // onKeyDown={handleSeatNoKeyDown}
                            />
                            {
                                seats.length > 1 && <Close 
                                onClick={() => handleRemoveSeat(seatIndex)}
                                className={addTicketStyles.closeSeat}
                            />
                            }
                        </div>
                    ))}
                    <div key = {`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo`}>
                        <div key = {`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo`} 
                            className={addTicketStyles.seatPlusbtn}
                            onClick={() => {
                                const updatedSeats = [...seats];
                                updatedSeats[seats.length] = { value: "" };
                                setValue(`ticketDetails.${eventIndex}.tickets.${ticketIndex}.seatNo`, updatedSeats);
                                trigger()
                            }}
                        >
                            <label className={addTicketStyles.plusLabelStyle}>+</label>
                        </div>
                    </div>
                </div>
                <div>
                    <p className={addTicketStyles.errorMsgStyle} style = {{marginLeft: "10px"}}>
                        {seatNoError}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TicketsList;
